import { useCallback, useContext } from 'react';
import { GlobalContext } from 'context';
import { getDecryptedValue } from '../utils/LocalStorageUtils';
import {
  useInitiateProxy,
  useTerminateProxy,
  ProxyAuthorizationInitiateRequest,
  ProxyTerminateRequest,
  ProxyAuthorizationRequestStatus,
  ProxyContactPoint,
} from '@sentara-health/sentara-api-hooks-core';

export enum PersonaType {
  Primary = 'Primary',
  Spouse = 'Spouse',
  Dependent = 'Dependents',
  Empty = ''
}


export const getPersonaType = (memberId: string): PersonaType => {
  const regex = /\*(\d{2})$/;
  const match = regex.exec(memberId || '');
  if (match) {
    const suffix = match[1];
    if (suffix === '01') return PersonaType.Primary;
    if (suffix === '02') return PersonaType.Spouse;
    return PersonaType.Dependent;
  }
  return PersonaType.Empty;
};

export interface InitiateProxyParams {
  designatorMemberId: string;
  designatorName: string;
  personaType: PersonaType;
  contactPoint?: ProxyContactPoint[];
}

export interface TerminateProxyParams {
  designatorMemberId: string;
}

const USER_TELECOM_CACHE_KEY = 'userTelecom';

const resolveUserTelecom = async (userTelecom: ProxyContactPoint[] = []) => {
  if (Array.isArray(userTelecom) && userTelecom.length > 0) {
    return userTelecom;
  }

  const cachedTelecom = await getDecryptedValue(USER_TELECOM_CACHE_KEY);
  if (!cachedTelecom) {
    return [];
  }

  if (Array.isArray(cachedTelecom)) {
    return cachedTelecom;
  }

  if (typeof cachedTelecom === 'string') {
    try {
      const parsedTelecom = JSON.parse(cachedTelecom);
      return Array.isArray(parsedTelecom) ? parsedTelecom : [];
    } catch {
      return [];
    }
  }

  return [];
};

export const useProxyAuthorization = () => {
  const { userTelecom } = useContext(GlobalContext);
  const { initiateProxyAuthorization: initiateProxyAPI } = useInitiateProxy();
  const { terminateProxyAuthorization: terminateProxyAPI } =
    useTerminateProxy();

  const initiateProxy = useCallback(
    async (params: InitiateProxyParams) => {
      const {
        designatorMemberId,
        designatorName,
        personaType,
      } = params;

      const finalContactPoint = await resolveUserTelecom(userTelecom);
      const nameParts = designatorName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.at(-1) || '';

      const requestData: ProxyAuthorizationInitiateRequest = {
        identifier: [
          {
            system: 'designatorMemberId',
            value: designatorMemberId,
          },
        ],
        humanName: {
          use: 'official',
          text: designatorName,
          family: lastName,
          given: [firstName],
        },
        contactPoint: finalContactPoint,
        proxies: [
          {
            PersonaType: personaType,
            identifier: [
              {
                system: 'designatorMemberId',
                value: designatorMemberId,
              },
            ],
            Consents: [
              {
                proxyAuthorizationRequestStatus:
                  ProxyAuthorizationRequestStatus.Approved,
                claims: ['Medical claims and authorizations'],
                confirmedBy: designatorName,
                designationStatus: 'Designator',
              },
            ],
            DisclosureOption: 'other',
            DisclosureOptionDescription: '',
            Acknowledgement: 'Yes',
          },
        ],
      };

      const response = await initiateProxyAPI(requestData);
      
      // Check if the API returned an error code
      if ((response as { errorCode?: string }).errorCode) {
        const errorCode = (response as unknown as { errorCode: string }).errorCode;
        throw new Error(`Initiate proxy failed with error code: ${errorCode}`);
      }
      
      return response;
    },
    [initiateProxyAPI, userTelecom]
  );

  const terminateProxy = useCallback(
    async (params: TerminateProxyParams) => {
      const { designatorMemberId } = params;

      const requestData: ProxyTerminateRequest = {
        identifier: [
          {
            system: 'designatorMemberId',
            value: designatorMemberId,
          },
        ],
      };

      const response = await terminateProxyAPI(requestData);
      
      // Check if the API returned an error code
      if ((response as { errorCode?: string }).errorCode) {
        const errorCode = (response as unknown as { errorCode: string }).errorCode;
        throw new Error(`Terminate proxy failed with error code: ${errorCode}`);
      }
      
      return response;
    },
    [terminateProxyAPI]
  );

  return {
    initiateProxy,
    terminateProxy,
    getPersonaType,
  };
};
